import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../../stores/authStore';
import { adminUsersService, type AdminUser } from '../../../services';

const UserCreatePage: React.FC = () => {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState<Partial<AdminUser>>({
    full_name: '',
    phone_number: '',
    role: 'customer',
    status: 'active',
    can_see_prices: true,
    can_see_labor: true,
    can_see_tax: true,
    can_see_stone_details: true,
    labor_fee_percent: 0,
    agency_profit_percent: 0,
    discount_percent: 0,
  });

  useEffect(() => {
    if (!user || (user.role !== 'admin' && user.role !== 'super_admin')) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, navigate]);

  const submit = async () => {
    if (!formData.full_name || !formData.phone_number) return;
    setSaving(true);
    try {
      await adminUsersService.createUser(formData);
      navigate('/admin/users', { replace: true });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4 lg:p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">ایجاد کاربر جدید</h1>
          <p className="text-gray-500 mt-1">اطلاعات کاربر را وارد کنید و ذخیره نمایید</p>
        </div>
        <div className="flex items-center gap-3">
          <button 
            className="px-6 py-2.5 rounded-xl text-gray-600 bg-white border border-gray-200 hover:bg-gray-50 font-medium transition-colors" 
            onClick={() => navigate('/admin/users')}
          >
            انصراف
          </button>
          <button 
            className="px-6 py-2.5 rounded-xl bg-[#0e444e] text-white hover:bg-[#0b363e] shadow-lg shadow-[#0e444e]/20 font-medium transition-all disabled:opacity-50 disabled:shadow-none" 
            onClick={submit} 
            disabled={saving || !(formData.full_name && formData.phone_number)}
          >
            {saving ? 'در حال ایجاد...' : 'ایجاد کاربر'}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Basic Info */}
        <div className="card h-fit">
          <h3 className="text-lg font-bold text-gray-900 mb-6 border-b pb-4">اطلاعات پایه</h3>
          <div className="space-y-4">
            <div>
              <label className="input-label">نام کامل</label>
              <input 
                className="input-field" 
                placeholder="نام و نام خانوادگی" 
                value={formData.full_name || ''} 
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })} 
              />
            </div>
            <div>
              <label className="input-label">شماره موبایل</label>
              <input 
                className="input-field" 
                placeholder="0912..." 
                dir="ltr"
                value={formData.phone_number || ''} 
                onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })} 
              />
            </div>
            <div>
              <label className="input-label">نقش کاربر</label>
              <select 
                className="input-field" 
                value={formData.role || 'customer'} 
                onChange={(e) => setFormData({ ...formData, role: e.target.value as AdminUser['role'] })}
              >
                <option value="customer">مشتری</option>
                <option value="wholesale">عمده‌فروش</option>
                <option value="retailer">خرده‌فروش</option>
                <option value="goldsmith">طلاساز</option>
                <option value="admin">ادمین</option>
                <option value="super_admin">مدیر سیستم</option>
              </select>
            </div>
            <div>
              <label className="input-label">وضعیت حساب</label>
              <select 
                className="input-field" 
                value={formData.status || 'active'} 
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              >
                <option value="active">فعال</option>
                <option value="inactive">غیرفعال</option>
                <option value="banned">مسدود شده</option>
              </select>
            </div>
          </div>
        </div>

        {/* Access & Pricing */}
        <div className="space-y-6">
          {/* Pricing Config */}
          <div className="card">
            <h3 className="text-lg font-bold text-gray-900 mb-6 border-b pb-4">تنظیمات مالی</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="input-label">درصد اجرت</label>
                <div className="relative">
                  <input 
                    type="number" 
                    className="input-field pl-8" 
                    value={formData.labor_fee_percent || 0} 
                    onChange={(e) => setFormData({ ...formData, labor_fee_percent: Number(e.target.value) })} 
                  />
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">%</span>
                </div>
              </div>
              <div>
                <label className="input-label">درصد سود (نمایندگی)</label>
                <div className="relative">
                  <input 
                    type="number" 
                    className="input-field pl-8" 
                    value={formData.agency_profit_percent || 0} 
                    onChange={(e) => setFormData({ ...formData, agency_profit_percent: Number(e.target.value) })} 
                  />
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">%</span>
                </div>
              </div>
              <div>
                <label className="input-label">درصد تخفیف</label>
                <div className="relative">
                  <input 
                    type="number" 
                    className="input-field pl-8" 
                    value={formData.discount_percent || 0} 
                    onChange={(e) => setFormData({ ...formData, discount_percent: Number(e.target.value) })} 
                  />
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">%</span>
                </div>
              </div>
            </div>
          </div>

          {/* Permissions */}
          <div className="card">
            <h3 className="text-lg font-bold text-gray-900 mb-4">دسترسی‌ها</h3>
            <div className="space-y-3">
              <label className="flex items-center justify-between p-3 rounded-xl bg-gray-50 border border-gray-100 cursor-pointer hover:bg-gray-100 transition-colors">
                <span className="text-sm font-medium text-gray-700">نمایش قیمت محصولات</span>
                <input 
                  type="checkbox" 
                  className="w-5 h-5 text-[--color-primary] rounded border-gray-300 focus:ring-[--color-primary]"
                  checked={!!formData.can_see_prices} 
                  onChange={(e) => setFormData({ ...formData, can_see_prices: e.target.checked })} 
                />
              </label>
              <label className="flex items-center justify-between p-3 rounded-xl bg-gray-50 border border-gray-100 cursor-pointer hover:bg-gray-100 transition-colors">
                <span className="text-sm font-medium text-gray-700">نمایش جزئیات اجرت</span>
                <input 
                  type="checkbox" 
                  className="w-5 h-5 text-[--color-primary] rounded border-gray-300 focus:ring-[--color-primary]"
                  checked={!!formData.can_see_labor} 
                  onChange={(e) => setFormData({ ...formData, can_see_labor: e.target.checked })} 
                />
              </label>
              <label className="flex items-center justify-between p-3 rounded-xl bg-gray-50 border border-gray-100 cursor-pointer hover:bg-gray-100 transition-colors">
                <span className="text-sm font-medium text-gray-700">نمایش مالیات</span>
                <input 
                  type="checkbox" 
                  className="w-5 h-5 text-[--color-primary] rounded border-gray-300 focus:ring-[--color-primary]"
                  checked={!!formData.can_see_tax} 
                  onChange={(e) => setFormData({ ...formData, can_see_tax: e.target.checked })} 
                />
              </label>
              <label className="flex items-center justify-between p-3 rounded-xl bg-gray-50 border border-gray-100 cursor-pointer hover:bg-gray-100 transition-colors">
                <span className="text-sm font-medium text-gray-700">نمایش جزئیات سنگ</span>
                <input 
                  type="checkbox" 
                  className="w-5 h-5 text-[--color-primary] rounded border-gray-300 focus:ring-[--color-primary]"
                  checked={!!formData.can_see_stone_details} 
                  onChange={(e) => setFormData({ ...formData, can_see_stone_details: e.target.checked })} 
                />
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserCreatePage;
